import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  MoreVertical,
  Pin,
  RotateCcw,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getPageNumbers } from '@/lib/pagination-utils';
import { cn } from '@/lib/utils';

export interface DataTableColumn {
  id: string;
  label: string;
  sortKey?: string;
  align?: 'left' | 'right';
  defaultWidth?: number;
  className?: string;
}

export interface DataTablePagination {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

export interface DataTableProps<T> {
  readonly columns: DataTableColumn[];
  readonly data: T[];
  readonly getRowId: (item: T) => string | number;
  readonly renderCell: (columnId: string, item: T) => React.ReactNode;

  readonly sortField?: string | null;
  readonly sortDirection?: 'asc' | 'desc';
  readonly onSort?: (field: string) => void;

  readonly showCheckbox?: boolean;
  readonly selectedIds?: Set<string | number>;
  readonly allSelected?: boolean;
  readonly onSelectAll?: (checked: boolean) => void;
  readonly onSelectRow?: (id: string | number, checked: boolean) => void;

  readonly isLoading?: boolean;
  readonly loadingContent?: React.ReactNode;
  readonly emptyContent?: React.ReactNode;

  readonly pagination?: DataTablePagination;

  readonly className?: string;

  readonly initialColumnOrder?: string[];
  readonly initialColumnWidths?: Record<string, number>;
  readonly initialPinnedLeft?: string[];
  readonly initialPinnedRight?: string[];
  readonly onColumnOrderChange?: (order: string[]) => void;
  readonly onColumnWidthsChange?: (widths: Record<string, number>) => void;
  readonly onPinnedChange?: (left: string[], right: string[]) => void;
  readonly onResetColumnOrder?: () => void;
  readonly onResetColumnWidths?: () => void;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  renderCell,
  sortField,
  sortDirection,
  onSort,
  showCheckbox,
  selectedIds,
  allSelected,
  onSelectAll,
  onSelectRow,
  isLoading,
  loadingContent,
  emptyContent,
  pagination,
  className,
  initialColumnOrder,
  initialColumnWidths,
  initialPinnedLeft,
  initialPinnedRight,
  onColumnOrderChange,
  onColumnWidthsChange,
  onPinnedChange,
  onResetColumnOrder,
  onResetColumnWidths,
}: DataTableProps<T>) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => initialColumnWidths ?? {});
  const columnWidthsRef = useRef<Record<string, number>>(columnWidths);
  columnWidthsRef.current = columnWidths;
  const resizingRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => initialColumnOrder ?? []);

  const [pinnedLeft, setPinnedLeft] = useState<string[]>(() => initialPinnedLeft ?? []);
  const [pinnedRight, setPinnedRight] = useState<string[]>(() => initialPinnedRight ?? []);

  useEffect(() => {
    if (initialColumnWidths) {
      setColumnWidths((prev) =>
        JSON.stringify(prev) === JSON.stringify(initialColumnWidths) ? prev : initialColumnWidths,
      );
    }
  }, [initialColumnWidths]);
  useEffect(() => {
    if (initialColumnOrder) {
      setColumnOrder((prev) =>
        JSON.stringify(prev) === JSON.stringify(initialColumnOrder) ? prev : initialColumnOrder,
      );
    }
  }, [initialColumnOrder]);
  useEffect(() => {
    if (initialPinnedLeft) {
      setPinnedLeft((prev) => (JSON.stringify(prev) === JSON.stringify(initialPinnedLeft) ? prev : initialPinnedLeft));
    }
  }, [initialPinnedLeft]);
  useEffect(() => {
    if (initialPinnedRight) {
      setPinnedRight((prev) =>
        JSON.stringify(prev) === JSON.stringify(initialPinnedRight) ? prev : initialPinnedRight,
      );
    }
  }, [initialPinnedRight]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollContainerWidth, setScrollContainerWidth] = useState(0);

  const updateScrollContainerWidth = useCallback(() => {
    if (scrollContainerRef.current) {
      setScrollContainerWidth(scrollContainerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    updateScrollContainerWidth();
    const observer = new ResizeObserver(updateScrollContainerWidth);
    if (scrollContainerRef.current) {
      observer.observe(scrollContainerRef.current);
    }
    return () => observer.disconnect();
  }, [updateScrollContainerWidth]);

  const orderedColumns = useMemo(() => {
    if (columnOrder.length === 0) return columns;
    const colIds = columns.map((c) => c.id);
    return columnOrder
      .filter((id) => colIds.includes(id))
      .concat(colIds.filter((id) => !columnOrder.includes(id)))
      .map((id) => columns.find((c) => c.id === id)!)
      .filter(Boolean);
  }, [columns, columnOrder]);

  const handleResizeMouseDown = (e: React.MouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId: id, startX, startWidth } = resizingRef.current;
      const delta = ev.clientX - startX;
      const newWidth = Math.max(60, startWidth + delta);
      setColumnWidths((prev) => ({
        ...prev,
        [id]: newWidth,
      }));
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        const currentWidths = columnWidthsRef.current;
        const finalWidths = { ...currentWidths };
        onColumnWidthsChange?.(finalWidths);
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handlePinLeft = (columnId: string) => {
    const newPinnedLeft = pinnedLeft.includes(columnId)
      ? pinnedLeft.filter((id) => id !== columnId)
      : [...pinnedLeft, columnId];
    const newPinnedRight = pinnedRight.filter((id) => id !== columnId);
    setPinnedLeft(newPinnedLeft);
    setPinnedRight(newPinnedRight);
    onPinnedChange?.(newPinnedLeft, newPinnedRight);
  };

  const handlePinRight = (columnId: string) => {
    const newPinnedRight = pinnedRight.includes(columnId)
      ? pinnedRight.filter((id) => id !== columnId)
      : [...pinnedRight, columnId];
    const newPinnedLeft = pinnedLeft.filter((id) => id !== columnId);
    setPinnedRight(newPinnedRight);
    setPinnedLeft(newPinnedLeft);
    onPinnedChange?.(newPinnedLeft, newPinnedRight);
  };

  const handleResetColumnWidths = () => {
    setColumnWidths({});
    if (onResetColumnWidths) {
      onResetColumnWidths();
    } else {
      onColumnWidthsChange?.({});
    }
  };
  const handleResetColumnOrder = () => {
    setColumnOrder([]);
    setPinnedLeft([]);
    setPinnedRight([]);
    if (onResetColumnOrder) {
      onResetColumnOrder();
    } else {
      onColumnOrderChange?.([]);
      onPinnedChange?.([], []);
    }
  };

  const getSortIcon = (sortKey: string) => {
    if (sortField !== sortKey) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    if (sortDirection === 'asc') return <ArrowUp className="h-4 w-4" strokeWidth={2.5} />;
    return <ArrowDown className="h-4 w-4" strokeWidth={2.5} />;
  };

  const totalColCount = orderedColumns.length + (showCheckbox ? 1 : 0);

  const pageSizeOptions = pagination?.pageSizeOptions ?? [10, 25, 50, 100];

  const pageNumbers = pagination ? getPageNumbers(pagination.currentPage, pagination.totalPages) : [];

  return (
    <div className={cn('flex flex-col flex-1 min-h-0', className)}>
      {/* Scrollable table area */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-auto">
        <table className="w-full caption-bottom text-sm [&_tr]:divide-x [&_tr]:divide-gray-100">
          <thead className="sticky top-0 z-10 bg-white shadow-sm">
            <tr>
              {showCheckbox && (
                <th className="h-10 text-center font-bold text-gray-700 w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-200">
                  <div className="flex items-center justify-center h-full">
                    <Checkbox checked={allSelected} onCheckedChange={(checked) => onSelectAll?.(checked === true)} />
                  </div>
                </th>
              )}
              {orderedColumns.map((col) => {
                const colWidth = columnWidths[col.id] ?? col.defaultWidth;
                const isDragOver = dragOverColumn === col.id && draggedColumn !== col.id;
                const isPinLeft = pinnedLeft.includes(col.id);
                const isPinRight = pinnedRight.includes(col.id);
                const pinLeftOffset = pinnedLeft.indexOf(col.id) * (colWidth || 150);
                const pinRightOffset = pinnedRight.indexOf(col.id) * (colWidth || 150);

                return (
                  <th
                    key={col.id}
                    draggable
                    onDragStart={() => setDraggedColumn(col.id)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverColumn(col.id);
                    }}
                    onDragEnd={() => {
                      setDraggedColumn(null);
                      setDragOverColumn(null);
                    }}
                    onDrop={() => {
                      if (!draggedColumn || draggedColumn === col.id) return;
                      const currentOrder = columnOrder.length > 0 ? [...columnOrder] : columns.map((c) => c.id);
                      const fromIdx = currentOrder.indexOf(draggedColumn);
                      const toIdx = currentOrder.indexOf(col.id);
                      currentOrder.splice(fromIdx, 1);
                      currentOrder.splice(toIdx, 0, draggedColumn);
                      setColumnOrder(currentOrder);
                      onColumnOrderChange?.(currentOrder);
                      setDraggedColumn(null);
                      setDragOverColumn(null);
                    }}
                    style={{
                      width: colWidth ? `${colWidth}px` : undefined,
                      minWidth: colWidth ? `${colWidth}px` : undefined,
                      position: isPinLeft || isPinRight ? 'sticky' : 'relative',
                      left: isPinLeft ? `${pinLeftOffset}px` : undefined,
                      right: isPinRight ? `${pinRightOffset}px` : undefined,
                      zIndex: isPinLeft || isPinRight ? 20 : undefined,
                    }}
                    className={cn(
                      'h-10 px-4 text-left font-bold text-gray-700 border-r border-gray-200 last:border-r-0 select-none bg-white',
                      isDragOver && 'bg-blue-50 border-l-2 border-l-blue-400',
                      draggedColumn === col.id && 'opacity-50',
                      (isPinLeft || isPinRight) && 'shadow-md',
                      col.className,
                    )}
                  >
                    <div className="flex items-center justify-between gap-1 w-full">
                      {col.sortKey && onSort ? (
                        <button
                          onClick={() => onSort(col.sortKey!)}
                          className="flex items-center gap-1 hover:text-gray-900 transition-colors font-bold"
                        >
                          <span>{col.label}</span>
                          <span className="text-gray-500">{getSortIcon(col.sortKey)}</span>
                        </button>
                      ) : (
                        <span className="font-bold">{col.label}</span>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <button className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                            <MoreVertical className="h-4 w-4 text-gray-300 hover:text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-[9999] w-48">
                          <DropdownMenuItem onClick={() => handlePinLeft(col.id)}>
                            <Pin className="h-4 w-4 mr-2" />
                            {isPinLeft ? 'Unpin from Left' : 'Pin to Left'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePinRight(col.id)}>
                            <Pin className="h-4 w-4 mr-2 rotate-180" />
                            {isPinRight ? 'Unpin from Right' : 'Pin to Right'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleResetColumnWidths}>
                            <Maximize2 className="h-4 w-4 mr-2" />
                            Reset Column Widths
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleResetColumnOrder}>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Reset Column Order
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {/* Resize handle */}
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-300 opacity-0 hover:opacity-60 transition-opacity z-10"
                      onMouseDown={(e) =>
                        handleResizeMouseDown(
                          e,
                          col.id,
                          colWidth || (e.currentTarget.closest('th') as HTMLElement)?.offsetWidth || 150,
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={totalColCount} className="h-64 p-0">
                  <div
                    className="sticky left-0 flex items-center justify-center h-64"
                    style={{ width: scrollContainerWidth > 0 ? `${scrollContainerWidth}px` : '100%' }}
                  >
                    {loadingContent ?? (
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <p className="text-sm text-muted-foreground">Loading...</p>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && data.length === 0 && (
              <tr>
                <td colSpan={totalColCount} className="h-64 p-0">
                  <div
                    className="sticky left-0 flex items-center justify-center h-64"
                    style={{ width: scrollContainerWidth > 0 ? `${scrollContainerWidth}px` : '100%' }}
                  >
                    {emptyContent ?? <p className="text-sm text-muted-foreground">No data</p>}
                  </div>
                </td>
              </tr>
            )}
            {!isLoading &&
              data.length > 0 &&
              data.map((item, index) => (
                <tr
                  key={getRowId(item)}
                  className={cn(index % 2 === 0 ? 'bg-white' : 'bg-gray-50', 'hover:bg-muted/30 transition-colors')}
                >
                  {showCheckbox && (
                    <td className="h-10 text-center w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-100">
                      <div className="flex items-center justify-center h-full">
                        <Checkbox
                          checked={selectedIds?.has(getRowId(item))}
                          onCheckedChange={(checked) => onSelectRow?.(getRowId(item), checked === true)}
                        />
                      </div>
                    </td>
                  )}
                  {orderedColumns.map((col) => {
                    const colWidth = columnWidths[col.id] ?? col.defaultWidth;
                    const isPinLeft = pinnedLeft.includes(col.id);
                    const isPinRight = pinnedRight.includes(col.id);
                    const pinLeftOffset = pinnedLeft.indexOf(col.id) * (colWidth || 150);
                    const pinRightOffset = pinnedRight.indexOf(col.id) * (colWidth || 150);

                    return (
                      <td
                        key={col.id}
                        style={{
                          width: colWidth ? `${colWidth}px` : undefined,
                          minWidth: colWidth ? `${colWidth}px` : undefined,
                          position: isPinLeft || isPinRight ? 'sticky' : undefined,
                          left: isPinLeft ? `${pinLeftOffset}px` : undefined,
                          right: isPinRight ? `${pinRightOffset}px` : undefined,
                          zIndex: isPinLeft || isPinRight ? 20 : undefined,
                        }}
                        className={cn(
                          'px-4 py-2 align-middle border-r border-gray-100 last:border-r-0',
                          (isPinLeft || isPinRight) && 'shadow-md',
                          (isPinLeft || isPinRight) && (index % 2 === 0 ? 'bg-white' : 'bg-gray-50'),
                        )}
                      >
                        {renderCell(col.id, item)}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {pagination && (
        <div className="shrink-0 border-t px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <Select
              value={pagination.pageSize.toString()}
              onValueChange={(val) => pagination.onPageSizeChange(Number(val))}
            >
              <SelectTrigger className="h-8 w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageNumbers.map((page, index) =>
                typeof page === 'string' ? (
                  <span key={`ellipsis-${index}`} className="px-1 text-muted-foreground">
                    ...
                  </span>
                ) : (
                  <Button
                    key={`page-${page}`}
                    variant={pagination.currentPage === page ? 'default' : 'outline'}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => pagination.onPageChange(page as number)}
                  >
                    {page}
                  </Button>
                ),
              )}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
